import { memo, RefAttributes } from 'react';
import { Button as RACButton, ButtonProps as RACButtonProps } from 'react-aria-components';

import { ComponentContextProps, merge, renderWithContext, useComponent } from '@/components';

export type ButtonProps = RACButtonProps & Partial<ComponentContextProps> & RefAttributes<HTMLButtonElement>;

const IButton = ({ variant, className, children, ...rest }: ButtonProps) => {
  const context = useComponent({ variant });

  /* prettier-ignore */
  const styles = [
    'cursor-pointer',
    'data-[disabled]:cursor-not-allowed',
    'flex',
    'flex-row',
    'gap-sm',
    'group/button',
    'items-center',
    'justify-center',
    'outline-none',
    'py-1.25',
    'px-2',
    'rounded-md',
    'shadow-xs',
  ];

  /* prettier-ignore */
  const plainStyles = [
    'shadow-none',

    /* focus */
    'focus:bg-gray-100',

    /* hover */
    'hover:bg-gray-50',

    /* pressed */
    'data-[pressed]:bg-gray-100',

    /* FilterCard */
    'group-[*]/filter-card:hover:bg-primary-50',
    'group-[*]/filter-card:bg-primary-50',

    /* Tooltip */
    'group-[*]/tooltip:bg-transparent',
    'group-[*]/tooltip:p-0',

    /* SelectPills */
    'group-[*]/select-pills:border-1',
    'group-[*]/select-pills:border-gray-200',
    'group-[*]/select-pills:rounded-md',
    'group-[*]/select-pills:bg-gray-25',
    'data-[selected]:group-[*]/select-pills:bg-gray-25',
    'data-[pressed]:group-[*]/select-pills:bg-gray-25',
    'focus:group-[*]/select-pills:bg-gray-25',
  ];

  /* prettier-ignore */
  const primaryStyles = [
    /* basic */
    'bg-linear-to-b',
    'border-1',
    'border-primary-500',
    'from-primary-400',
    'to-primary-500',

    /* basic (Tooltip) */
    'group-[*]/tooltip:border-gray-500',
    'group-[*]/tooltip:px-3',
    'group-[*]/tooltip:from-transparent',
    'group-[*]/tooltip:to-transparent',

    /* focus */
    'focus:border-primary-700',
    'focus:from-primary-500',
    'focus:to-primary-600',

    /* focus (Tooltip) */
    'focus:group-[*]/tooltip:!border-gray-700',
    'focus:group-[*]/tooltip:!from-gray-700',
    'focus:group-[*]/tooltip:!to-gray-700',

    /* hover */
    'hover:border-primary-700',
    'hover:from-primary-500',
    'hover:to-primary-600',

    /* hover (Tooltip) */
    'hover:group-[*]/tooltip:!border-gray-600',
    'hover:group-[*]/tooltip:!from-gray-600',
    'hover:group-[*]/tooltip:!to-gray-600',

    /* pressed */
    'data-[pressed]:border-primary-700',
    'data-[pressed]:from-primary-600',
    'data-[pressed]:to-primary-700',

    /* pressed (Tooltip) */
    'data-[pressed]:group-[*]/tooltip:!border-gray-500',
    'data-[pressed]:group-[*]/tooltip:!from-transparent',
    'data-[pressed]:group-[*]/tooltip:!to-transparent',

    /* disabled */
    'data-[disabled]:border-gray-200',
    'data-[disabled]:from-white',
    'data-[disabled]:to-white',

    /* disabled (Tooltip) */
    'data-[disabled]:group-[*]/tooltip:!border-gray-600',
    'data-[disabled]:group-[*]/tooltip:!from-transparent',
    'data-[disabled]:group-[*]/tooltip:!to-transparent',
  ];

  /* prettier-ignore */
  const secondaryStyles = [
    'border-1',

    /* basic */
    'border-gray-300',
    'bg-white',

    /* focus */
    'focus:border-gray-300',
    'focus:bg-gray-50',

    /* hover */
    'hover:border-gray-300',
    'hover:bg-gray-50',

    /* pressed */
    'data-[pressed]:border-gray-300',
    'data-[pressed]:bg-gray-100',

    /* disabled */
    'data-[disabled]:border-gray-200',
    'data-[disabled]:bg-white',

    /* Select */
    'group-[*]/select:w-full',
    'group-[*]/select:justify-start',
    'group-[*]/select:border-1',
    'group-[*]/select:border-gray-200',
    'group-[*]/select:shadow-xs',

    /* Tooltip */
    'group-[*]/tooltip:py-1',
  ];

  /* prettier-ignore */
  const tertiaryStyles = [
    'border-1',

    /* basic */
    'border-gray-300',
    'bg-white',

    /* focus */
    'focus:border-gray-300',
    'focus:bg-gray-100',

    /* hover */
    'hover:border-gray-300',
    'hover:bg-gray-100',

    /* pressed */
    'data-[pressed]:border-gray-300',
    'data-[pressed]:bg-gray-100',

    /* disabled */
    'data-[disabled]:border-gray-200',
    'data-[disabled]:bg-white',
  ];

  /* prettier-ignore */
  const dangerStyles = [
    'border-1',
    'border-danger-300',

    /* focus */
    'focus:bg-danger-50',
    'focus:border-danger-400',

    /* hover */
    'hover:bg-danger-25',
    'hover:border-danger-400',

    /* pressed */
    'data-[pressed]:border-danger-400',

    /* disabled */
    'data-[disabled]:border-gray-200',
  ];

  return (
    <RACButton
      className={merge(
        styles,
        context.variant === 'default' && plainStyles,
        context.variant === 'plain' && plainStyles,
        context.variant === 'primary' && primaryStyles,
        context.variant === 'secondary' && secondaryStyles,
        context.variant === 'tertiary' && tertiaryStyles,
        context.variant === 'danger' && dangerStyles,
        className
      )}
      {...rest}
    >
      {renderWithContext(children, context)}
    </RACButton>
  );
};

export const Button = memo(IButton) as typeof IButton;
